import { useCallback, useEffect, useState } from 'react';
import type { BookFilterState, BookSavedView, SavedViewsStore } from '../types/manageBooks';

const KEY = 'learnleaf.manageBooks.savedViews';
const MAX_VIEWS = 20;

function read(): SavedViewsStore {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return { version: 1, views: [] };
    const parsed = JSON.parse(raw) as Partial<SavedViewsStore>;
    if (parsed.version !== 1 || !Array.isArray(parsed.views)) {
      return { version: 1, views: [] };
    }
    return { version: 1, views: parsed.views };
  } catch {
    return { version: 1, views: [] };
  }
}

function write(store: SavedViewsStore) {
  localStorage.setItem(KEY, JSON.stringify(store));
}

function makeId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `v_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

export function useBookSavedViews() {
  const [views, setViews] = useState<BookSavedView[]>(() => read().views);

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === KEY) setViews(read().views);
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const save = useCallback((name: string, filters: BookFilterState): BookSavedView | null => {
    if (views.length >= MAX_VIEWS) return null;
    const view: BookSavedView = {
      id: makeId(),
      name,
      createdAt: new Date().toISOString(),
      filters,
    };
    const next = [...views, view];
    write({ version: 1, views: next });
    setViews(next);
    return view;
  }, [views]);

  const rename = useCallback((id: string, name: string) => {
    const next = views.map(v => (v.id === id ? { ...v, name } : v));
    write({ version: 1, views: next });
    setViews(next);
  }, [views]);

  const remove = useCallback((id: string) => {
    const next = views.filter(v => v.id !== id);
    write({ version: 1, views: next });
    setViews(next);
  }, [views]);

  return { views, save, rename, remove, max: MAX_VIEWS };
}
